import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, errorMessage, type AuthStatus } from "../api";
import { ApprovalQueue } from "../components/ApprovalQueue";
import { DecisionCard } from "../components/DecisionCard";
import { EmptyState, useToast } from "../components/feedback";
import { HoldButton } from "../components/HoldButton";
import { AgentSettingsPanel } from "../components/AgentSettingsPanel";
import { ProviderAuthPanel } from "../components/ProviderAuthPanel";
import { dateTime } from "../format";
import { Icon } from "../icons";

export function Decisions() {
  const pending = useQuery({ queryKey: ["decisions"], queryFn: () => api.decisions("PENDING") });
  const all = useQuery({ queryKey: ["decisions", "all"], queryFn: () => api.decisions() });
  const history = (all.data ?? []).filter((d) => d.state !== "PENDING").slice(0, 12);
  const list = pending.data ?? [];
  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <h1>
            Wählen und <span className="glow-word">entscheiden</span>
          </h1>
          <p className="hero-sub">
            Jede Entscheidung ist an Spec, Plan und Rolle gebunden. Ändert sich etwas, verfällt sie.
          </p>
        </div>
      </section>
      <ApprovalQueue />
      {list.length === 0 && !pending.isLoading ? (
        <div className="panel panel--section">
          <EmptyState
            icon="inbox"
            title="Nichts offen"
            text="Rückfragen von Agenten, Reviews und Release-Freigaben erscheinen hier."
          />
        </div>
      ) : (
        <section className="decision-grid">
          {list.map((d) => (
            <article key={d.id} className="panel decision">
              {d.workOrderId && (
                <Link to="/work-orders/$orderId" params={{ orderId: d.workOrderId }} className="link small">
                  Zum Auftrag <Icon name="arrow" />
                </Link>
              )}
              <DecisionCard decision={d} />
            </article>
          ))}
        </section>
      )}
      {history.length > 0 && (
        <section className="panel history" aria-labelledby="historyTitle">
          <h2 id="historyTitle">Verlauf</h2>
          <ul className="items">
            {history.map((d) => (
              <li key={d.id}>
                <span className={`ic ${d.state === "RESOLVED" ? "ok" : "unk"}`}>
                  <Icon name={d.state === "RESOLVED" ? "check" : "clock"} />
                </span>
                <span>
                  {d.question}
                  <small className="muted block">
                    {d.state === "RESOLVED"
                      ? `Entschieden: ${d.options.find((o) => o.id === d.resolvedOptionId)?.title ?? ""} · ${d.resolvedAt ? dateTime(d.resolvedAt) : ""}`
                      : d.state === "INVALIDATED"
                        ? "Verfallen durch neue Revision"
                        : "Abgelaufen"}
                  </small>
                </span>
                <span className={`state state--${d.state === "RESOLVED" ? "verified" : "unknown"}`}>
                  {d.state.toLowerCase()}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

export function Agents({ auth }: { auth: AuthStatus }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const nodes = useQuery({ queryKey: ["nodes"], queryFn: api.nodes });
  const kill = useQuery({ queryKey: ["killswitch"], queryFn: api.killSwitch });
  const isOwner = auth.user?.role === "owner";
  const code = useMutation({
    mutationFn: api.createNodeCode,
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const copyCode = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast("Registrierungscode in die Zwischenablage kopiert");
    } catch {
      toast("Kopieren nicht möglich. Bitte den Code markieren und von Hand kopieren.", "error");
    }
  };
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeNode(id),
    onSuccess: async () => {
      toast("Node widerrufen, laufende Leases beendet");
      await queryClient.invalidateQueries({ queryKey: ["nodes"] });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const trigger = useMutation({
    mutationFn: () => api.triggerKillSwitch("In der Agentenansicht ausgelöst"),
    onSuccess: async (r) => {
      toast(`Kill Switch aktiv: ${r.cancelledRuns} Läufe gestoppt`);
      await queryClient.invalidateQueries();
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const release = useMutation({
    mutationFn: api.releaseKillSwitch,
    onSuccess: async () => {
      toast("Kill Switch aufgehoben, blockierte Aufträge laufen weiter");
      await queryClient.invalidateQueries();
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });

  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <h1>
            Agenten und <span className="glow-word">Werkstatt.</span>
          </h1>
          <p className="hero-sub">
            szyOS Nodes holen Arbeit nur ausgehend ab. Jeder Lauf läuft unter einer befristeten Freigabe
            (Lease): Der Node darf nur so lange daran arbeiten, wie sie gilt. Wird sie widerrufen, stoppt der
            Lauf.
          </p>
        </div>
      </section>
      <section className="agents-grid">
        <div className="panel" aria-labelledby="nodesTitle">
          <div className="panel-head">
            <h2 id="nodesTitle">Nodes</h2>
          </div>
          {(nodes.data ?? []).length === 0 && (
            <EmptyState
              icon="server"
              title="Kein Node verbunden"
              text="Starte lokal mit pnpm dev; der Entwicklungs-Node meldet sich automatisch an."
            />
          )}
          <ul className="items">
            {(nodes.data ?? []).map((n) => (
              <li key={n.id}>
                <span className={`ic ${n.state === "ONLINE" ? "ok" : n.state === "REVOKED" ? "blk" : "unk"}`}>
                  <Icon name="server" />
                </span>
                <span>
                  {n.name}
                  <small className="muted block">
                    {n.capabilities.join(", ")} · Version {n.version}
                    {n.lastHeartbeatAt ? ` · Heartbeat ${dateTime(n.lastHeartbeatAt)}` : ""}
                  </small>
                  {n.authModes.length > 0 && (
                    <small className="auth-modes block">
                      {n.authModes.includes("subscription") && (
                        <span className="chip chip--ghost">
                          <Icon name="key" /> Abo-Modus eingerichtet
                        </span>
                      )}
                      {n.authModes.includes("api_key") && (
                        <span className="chip chip--ghost">
                          <Icon name="key" /> API-Modus eingerichtet
                        </span>
                      )}
                    </small>
                  )}
                </span>
                {isOwner && n.state !== "REVOKED" ? (
                  <button type="button" className="chip-btn is-danger" onClick={() => revoke.mutate(n.id)}>
                    Widerrufen
                  </button>
                ) : (
                  <span className={`state state--${n.state === "ONLINE" ? "verified" : "unknown"}`}>
                    {n.state.toLowerCase()}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {isOwner && (
            <div className="node-code">
              <button type="button" className="btn" onClick={() => code.mutate()} disabled={code.isPending}>
                <span className="btn-label">
                  <Icon name="key" /> Registrierungscode erzeugen
                </span>
              </button>
              {code.data && (
                <div className="node-code-result">
                  <p className="mono">
                    {code.data.code}{" "}
                    <span className="muted">gültig bis {dateTime(code.data.expiresAt)}, einmalig</span>
                  </p>
                  <button type="button" className="chip-btn" onClick={() => void copyCode(code.data.code)}>
                    <Icon name="file" /> Code kopieren
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="panel decision" aria-labelledby="killTitle">
          <div className="dec-top">
            <span className="dec-kicker">Sicherheit</span>
            {kill.data?.active && (
              <span className="badge-block">
                <Icon name="alert" /> aktiv
              </span>
            )}
          </div>
          <h2 className="dec-title" id="killTitle">
            <Icon name="power" /> <span>Kill Switch</span>
          </h2>
          <p className="dec-text">
            Stoppt alle Läufe im Workspace, widerruft Leases und blockiert laufende Aufträge. Neue Läufe
            starten erst nach dem Aufheben.
          </p>
          {!isOwner ? (
            <p className="muted">Nur der Owner kann den Kill Switch bedienen.</p>
          ) : kill.data?.active ? (
            <button
              type="button"
              className="btn"
              onClick={() => release.mutate()}
              disabled={release.isPending}
            >
              <span className="btn-label">Kill Switch aufheben</span>
            </button>
          ) : (
            <HoldButton
              onConfirm={() => trigger.mutate()}
              onTooShort={() => toast("Zum Auslösen gedrückt halten.")}
            >
              Halten &amp; alles stoppen
            </HoldButton>
          )}
          <h3 className="sub-head">Provider</h3>
          <ul className="items">
            {[
              ["Claude Code", "aktiv auf der Werkstatt (Abo)", "verified"],
              ["Codex CLI", "ab M4", "unknown"],
              ["Gemini CLI", "ab M4", "unknown"],
            ].map(([name, note, cls]) => (
              <li key={name}>
                <span className={`ic ${cls === "verified" ? "ok" : "unk"}`}>
                  <Icon name="bot" />
                </span>
                <span>{name}</span>
                <span className={`state state--${cls}`}>{note}</span>
              </li>
            ))}
          </ul>
        </div>
        <ProviderAuthPanel isOwner={isOwner} />
        <AgentSettingsPanel isOwner={isOwner} />
      </section>
    </>
  );
}
